import type { HTMLAttributeAnchorTarget } from 'react'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import BaseYouMayAlsoLikeTile from '../BaseYouMayAlsoLikeTile'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import offerPageURL from 'lib/offer/offerPageURL'
import HotelPriceContent from './HotelPriceContent'
import BedbankHotelPriceContent from './BedbankHotelPriceContent'
import BundledHotelPriceContent from './BundledHotelPriceContent'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import type { TileViewProps } from '../Common/types'
import { breakpointSizes } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useInView } from 'react-intersection-observer'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { useIntl } from 'react-intl'

const PriceDetailsColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
`

const Labels = styled(LabelGroup)``

// Need to force display flex because <Carousel /> keeps overwriting it as display: block.
const BaseTile = styled(BaseYouMayAlsoLikeTile)`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  &.force-flex.force-flex {
    display: flex;
  }

  ${BaseYouMayAlsoLikeTile.ImageBadges} {
    left: ${rem(8)};
    right: auto;
  }

  ${BaseYouMayAlsoLikeTile.PriceDetails} {
    display: flex;
    width: 100%;
    flex-wrap: wrap;

    ${PriceDetailsColumn}.deposit {
      flex: 1;
      min-width: 100%;
      margin-bottom: ${rem(8)};
    }
    ${PriceDetailsColumn}.price-amounts {
      flex: 1;
    }
  }

  ${BaseYouMayAlsoLikeTile.MainDescription} {
    overflow-y: auto;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    line-clamp: 5;
    overflow: hidden;

    .tile-body {
      margin-top: ${rem(4)};
    }

    ${Labels} {
      margin: ${rem(8)} 0 0 -1px;
    }
  }
`

const UrgencyLabelsWrapper = styled.div`
   display: flex;
   flex-wrap: wrap;
`

const ClickableWrapper = styled(ClickableLink)`
  display: flex;
  justify-content: center;
  width: ${rem(348)};
  min-height: ${rem(416)};
  flex-direction: column;

  & > *:first-child {
    flex: 1;
  }
`

export interface Props extends Omit<TileViewProps, 'pricing'>, Utils.PropsWithDataAttributes {
  anchorTarget?: HTMLAttributeAnchorTarget
  offer: App.Offer | App.OfferSummary | App.BedbankOffer
  linkQuery?: string
  position: number
  flightOrigin?: string
  alternativeCheckIn?: string
  alternativeCheckOut?: string
  alternativePrice?: number
  alternativeMemberPrice?: number
}

function YmalOfferTileSmall({
  anchorTarget,
  offer,
  linkQuery,
  position,
  flightOrigin,
  depositAmountPercentage,
  depositAmount,
  depositEnabled,
  depositThresholds,
  distance,
  distanceFrom,
  distanceUnit,
  duration,
  tourDepositType,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
  alternativeMemberPrice,
  ...rest
}: Props) {
  const intl = useIntl()
  const isBedbank = isBedbankOffer(offer)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const { image, property, walledGarden } = offer
  const title = property?.name
  const titleBody = isBedbank ? offer.tagline : offer.name
  const location = (() => {
    if (isBedbank) {
      if (offer.property.locationTitleOverride) return offer.property.locationTitleOverride
      return offer.property?.address?.city && offer.property?.address?.countryName
        ? intl.formatMessage(
            {
              id: 'LfEUAvms7d4GfJIER-fP',
              defaultMessage: '{city}, {country}',
              description: 'YMAL bedbank offer tile city and country separated by comma',
            },
            { city: offer.property.address.city, country: offer.property.address.countryName },
          )
        : (offer.property?.address?.city || offer.property?.address?.countryName || '')
    }
    return offer.locationHeading && offer.locationSubheading
      ? intl.formatMessage(
          {
            id: 'rlAZ_7VE5SKl6ZCUc_Mz',
            defaultMessage: '{locationHeading}, {locationSubheading}',
            description: 'YMAL offer tile location heading and subheading separated by comma',
          },
          { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
        )
      : (offer.locationHeading || offer.locationSubheading || '')
  })()
  const [inViewRef, hasBeenInView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const onClickSecureDeposit = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
  }, [])

  const fireProductClick = useCallback(() => {
    dispatchOfferListEvent({
      type: OfferListEvents.productClick,
      offer,
      position,
    })
  }, [offer, position, dispatchOfferListEvent])

  useEffect(() => {
    if (hasBeenInView) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer,
        position,
      })
    }
  }, [dispatchOfferListEvent, hasBeenInView, offer, position])

  const clickToDestination = useMemo(() => [offerPageURL(offer), linkQuery ?? ''].join('?'), [offer, linkQuery])
  const productType = isBedbank ? (offer.promotions?.length ? 'limited_time_special' : 'bedbank_hotel') : offer.productType

  return (
    <ProductPaletteProvider product={offer}>
      <ClickableWrapper to={clickToDestination} target={anchorTarget} onClick={fireProductClick} ref={inViewRef}>
        <BaseTile
          {...rest}
          className="force-flex"
          image={image}
          location={location}
          locationDistance={distance}
          locationDistanceFrom={distanceFrom}
          locationDistanceUnit={distanceUnit}
          title=""
          subtitle={null}
          walledGarden={walledGarden}
          variant="bordered"
          imageXSRatio="350:180"
          imageXSWidth={`${breakpointSizes.mobile.max}px`}
          imageSMRatio="350:180"
          imageSMWidth="350px"
        >
          <BaseTile.ImageBadges>
            <ProductTypeLabel productType={productType} />
          </BaseTile.ImageBadges>
          <BaseTile.ContentTags>
            {!isBedbank && <UrgencyLabelsWrapper data-testid="urgency-labels">
              <OfferLabels offer={offer} />
            </UrgencyLabelsWrapper>}
          </BaseTile.ContentTags>
          <BaseTile.MainDescription>
            {title && <Heading variant="heading6">{title}</Heading>}
            {titleBody && <BodyText variant="medium" className="tile-body">
              {titleBody}
            </BodyText>}
          </BaseTile.MainDescription>
          <BaseTile.PriceDetails>
            {depositEnabled && <PriceDetailsColumn className="deposit">
              <SecureWithDepositTagAndModal
                testId="secure-deposit-tag"
                depositThresholds={depositThresholds}
                depositAmount={depositAmount}
                depositAmountPercentage={depositAmountPercentage}
                depositType={tourDepositType}
                offerType={offer.type}
                onClick={onClickSecureDeposit}
              />
            </PriceDetailsColumn>}
            <PriceDetailsColumn className="price-amounts">
              {isBedbank && <BedbankHotelPriceContent
                duration={duration}
                offer={offer}
                alternativeCheckIn={alternativeCheckIn}
                alternativeCheckOut={alternativeCheckOut}
                alternativePrice={alternativePrice}
              />}
              {!isBedbank && !offer.bundledWithFlightsOnly && <HotelPriceContent
                duration={duration}
                offer={offer}
                flightOrigin={flightOrigin}
                alternativeCheckIn={alternativeCheckIn}
                alternativeCheckOut={alternativeCheckOut}
                alternativePrice={alternativePrice}
                alternativeMemberPrice={alternativeMemberPrice}
              />}
              {!isBedbank && offer.bundledWithFlightsOnly && <BundledHotelPriceContent duration={duration} offer={offer} />}
            </PriceDetailsColumn>
            <PriceDetailsColumn>
              <TextButton kind="primary">{intl.formatMessage({
                id: '4rISgZcFm1El05KQgj2Q',
                defaultMessage: 'View Offer',
                description: 'CTA button text to view an offer in the YMAL tile in the offer listing section',
              })}
              </TextButton>
            </PriceDetailsColumn>
          </BaseTile.PriceDetails>
        </BaseTile>
      </ClickableWrapper>
    </ProductPaletteProvider>
  )
}

export default YmalOfferTileSmall
